// qs-useLayerPositioning.ts
import { computed, ref } from 'vue'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useModeManager } from '@/composables/qs-useModeManager'
import { validateLayerOperation } from '@/utils/qs-util-layerLock'
import type { KonvaStage } from '@/types/qs-type-konva'

export function useLayerPositioning() {
  const canvasStore = useCanvasStore()
  const layerStore = useLayerStore()
  const modeManager = useModeManager()

  const isPositioningMode = computed(() => modeManager.activeMode.value === 'position')
  const positioningLayerId = computed(() => modeManager.globalModeState.value.activeLayerId)

  const isDragging = ref(false)
  const dragStartPos = ref({ x: 0, y: 0 })
  const layerStartPos = ref({ x: 0, y: 0 })

  const getLayerData = (layerId: string) => {
    return layerStore.layers.find(l => l.id === layerId)
  }

  const findLayerInStage = (stage: KonvaStage, layerId: string) => {
    try {
      return stage.findOne(`#layer-${layerId}`)
    } catch (error) {
      console.error('Error finding layer in stage:', error)
      return null
    }
  }

  const updateLayerPositionInStore = (layerId: string, position: { x: number; y: number }) => {
    try {
      const layerIndex = layerStore.layers.findIndex(l => l.id === layerId)
      if (layerIndex !== -1) {
        layerStore.layers[layerIndex].position = position
      }
    } catch (error) {
      console.error('Error updating layer position in store:', error)
    }
  }

  const getCanvasPosition = (e: MouseEvent | TouchEvent, stage: KonvaStage) => {
    try {
      if (!e || !stage) return { x: 0, y: 0 }

      const rect = stage.container().getBoundingClientRect()
      const scale = canvasStore.scale
      const position = canvasStore.position

      let clientX: number
      let clientY: number

      if ('touches' in e && e.touches.length > 0) {
        clientX = e.touches[0].clientX
        clientY = e.touches[0].clientY
      } else if ('clientX' in e) {
        clientX = e.clientX
        clientY = e.clientY
      } else {
        return { x: 0, y: 0 }
      }

      const canvasX = (clientX - rect.left) / scale - position.x / scale
      const canvasY = (clientY - rect.top) / scale - position.y / scale

      return { x: canvasX, y: canvasY }
    } catch (error) {
      console.error('Error getting canvas position:', error)
      return { x: 0, y: 0 }
    }
  }

  const canPositionLayer = (layerId: string | null): boolean => {
    if (!layerId) return false

    const layerData = getLayerData(layerId)
    if (!layerData) return false

    const validation = validateLayerOperation(layerId, 'transform')
    return validation.canProceed
  }

  const stopPositioning = () => {
    isDragging.value = false
    dragStartPos.value = { x: 0, y: 0 }
    layerStartPos.value = { x: 0, y: 0 }
  }

  const startDragging = (mousePos: { x: number; y: number }, stage: KonvaStage) => {
    if (!isPositioningMode.value || !positioningLayerId.value) return

    const layer = findLayerInStage(stage, positioningLayerId.value)
    if (!layer) return

    dragStartPos.value = { ...mousePos }
    layerStartPos.value = { x: layer.x(), y: layer.y() }
    isDragging.value = true
  }

  const updatePosition = (mousePos: { x: number; y: number }, stage: KonvaStage) => {
    if (!isDragging.value || !positioningLayerId.value) return

    const layer = findLayerInStage(stage, positioningLayerId.value)
    if (!layer) return

    const deltaX = mousePos.x - dragStartPos.value.x
    const deltaY = mousePos.y - dragStartPos.value.y

    const newX = layerStartPos.value.x + deltaX
    const newY = layerStartPos.value.y + deltaY

    layer.x(newX)
    layer.y(newY)
    stage.batchDraw()
  }

  const endDragging = (stage: KonvaStage) => {
    if (!isDragging.value || !positioningLayerId.value) return

    const layer = findLayerInStage(stage, positioningLayerId.value)
    if (!layer) return

    const finalPosition = { x: layer.x(), y: layer.y() }
    updateLayerPositionInStore(positioningLayerId.value, finalPosition)

    isDragging.value = false
    dragStartPos.value = { x: 0, y: 0 }
    layerStartPos.value = { x: 0, y: 0 }
  }

  const resetLayerPosition = (stage: KonvaStage, layerId: string) => {
    try {
      const layer = findLayerInStage(stage, layerId)
      if (layer) {
        layer.x(0)
        layer.y(0)
        stage.batchDraw()
        updateLayerPositionInStore(layerId, { x: 0, y: 0 })
      }
    } catch (error) {
      console.error('Error resetting layer position:', error)
    }
  }

  return {
    isPositioningMode,
    positioningLayerId,
    isDragging,
    stopPositioning,
    startDragging,
    updatePosition,
    endDragging,
    resetLayerPosition,
    getCanvasPosition,
    canPositionLayer
  }
}